import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ArrowRight, Bookmark, BookmarkPlus, Lightbulb, Loader2, Search, Sparkles, Trash2, X } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { cn } from "@/lib/utils";
import { useEffectivePermissions } from "@/hooks/use-effective-permissions";
import { describeAiError, parseApiError, type FriendlyAiError } from "@/lib/ai-error";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import type { NlFilterPage, NlFilterSpec, Region, SavedSmartSearch } from "@shared/schema";

// Page-specific natural-language sample sentences shown in the
// "Try an example" popover. Keep these short, concrete, and mapped to
// concepts the parser already understands (status, contact recency,
// quadrant, paid status, etc.).
const EXAMPLE_SENTENCES: Record<NlFilterPage, string[]> = {
  leads: [
    "Hot leads in TX I haven't contacted in 30 days",
    "Theaters in the South with no contact in 60 days",
    "Top-rated leads I haven't followed up on this month",
    "New leads from the West region",
    "Cold leads with rating 4 or higher",
  ],
  clients: [
    "Clients with shows this year that haven't been billed",
    "Casinos in the Midwest with events after June",
    "Clients I haven't talked to in 90 days",
    "Theaters with shows next quarter",
    "Top quadrant clients in California",
  ],
  invoices: [
    "Unpaid invoices over $500",
    "Paid invoices from last year",
    "Invoices for shows in 2025 that are still unpaid",
    "Unpaid invoices with no billing contact in 30 days",
    "All invoices for theaters this year",
  ],
  sponsor_leads: [
    "Sponsor leads I haven't contacted in 30 days",
    "Top-rated sponsor leads in the Midwest",
    "New sponsor leads from the West region",
    "Sponsor leads with rating 7 or higher",
    "Unconverted sponsor leads from this year",
  ],
  sponsor_clients: [
    "Sponsor clients in Texas",
    "Sponsor clients I haven't reached out to in 60 days",
    "Active sponsor clients from the South region",
    "Sponsor clients with events this year",
    "Top sponsor clients by rating",
  ],
};

export type SmartSearchBarProps = {
  page: NlFilterPage;
  // Plain-text fallback path. The parent owns the q-string and forwards
  // it to the existing spreadsheet/search prop. Smart-search results
  // also flow through this same callback (via `spec.q`) when the LLM
  // chooses to use it — one filter spec, one query layer.
  searchQuery: string;
  onSearchQueryChange: (q: string) => void;
  // Smart-search filter spec. The parent merges these with whatever
  // manual pill filters it already passes to the spreadsheet. Pass
  // `null` to clear.
  spec: NlFilterSpec | null;
  onSpecChange: (spec: NlFilterSpec | null) => void;
  // Per-user smart-search preference (resolved by /api/profile from
  // either the persisted boolean or the role-based default). When the
  // user toggles it we PATCH the preference and reflect it locally.
  smartEnabled: boolean;
  onSmartEnabledChange: (enabled: boolean) => void;
  // Optional notifier that fires on every change to the *typed* value
  // (including programmatic clears, escape, sentence-runs, and the
  // internal sync from `searchQuery`). Parents wire this up so the
  // "Did you mean…?" chip row can react while the user is still
  // typing — without requiring Enter or blur to commit the search to
  // the table. This is intentionally separate from
  // `onSearchQueryChange`, which still only fires on Enter/blur so the
  // table-level filter behaviour is preserved.
  onDraftChange?: (value: string) => void;
  // Workspace-level AI master switch (from /api/admin/ai-settings or
  // /api/ai/balance). Smart search is hidden when AI is off for the
  // workspace — falls back to plain search.
  aiEnabledForWorkspace: boolean;
  // Extra placeholder copy ("Search leads…" etc).
  placeholder?: string;
  // Region lookup so the chip row can show a friendly name instead of
  // the raw UUID for `regionIds`.
  regions?: Pick<Region, "id" | "name">[];
};

// User-friendly labels for spec keys; we hide noisy keys (like q, which
// is already shown in the input itself) and label the rest. Keep this
// list in lockstep with `nlFilterSpecSchema` in shared/schema.ts.
const SPEC_KEY_LABEL: Partial<Record<keyof NlFilterSpec, string>> = {
  clientType: "Type",
  talentId: "Talent",
  status: "Status",
  quadrants: "Quadrant",
  ratingMin: "Min rating",
  lastContactedDaysAgo: "No contact",
  venueType: "Venue",
  dateOfEventFrom: "Show date ≥",
  dateOfEventTo: "Show date ≤",
  paidStatus: "Paid",
  dateOfEventYear: "Year",
  billingLastContactDaysAgo: "No billing contact",
  regionIds: "Region",
};

function formatChipValue(
  key: keyof NlFilterSpec,
  value: unknown,
  regions: Pick<Region, "id" | "name">[],
): string {
  if (key === "regionIds" && Array.isArray(value)) {
    const names = value
      .map((id) => regions.find((r) => r.id === id)?.name || id)
      .filter(Boolean);
    return names.join(", ");
  }
  if (key === "quadrants" && Array.isArray(value)) {
    return value.join(", ");
  }
  if (key === "lastContactedDaysAgo" || key === "billingLastContactDaysAgo") {
    return `${value} days`;
  }
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

export function SmartSearchBar({
  page,
  searchQuery,
  onSearchQueryChange,
  spec,
  onSpecChange,
  smartEnabled,
  onSmartEnabledChange,
  onDraftChange,
  aiEnabledForWorkspace,
  placeholder,
  regions = [],
}: SmartSearchBarProps) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const { role } = useEffectivePermissions();
  const isAdmin = role === "admin";
  // Local "did the user actively toggle since last toast?" guard so we
  // don't flash the off-state hint on every render.
  const lastSpec = useRef(spec);

  // The natural-language input is independent of the substring-q the
  // existing spreadsheet uses. When smart-search is off (or the parser
  // fails), we fall back to writing into `searchQuery` directly so the
  // user keeps a useful experience.
  const [draft, setDraftState] = useState(searchQuery || "");
  // Keep the latest onDraftChange in a ref so the wrapper below stays
  // stable across renders without re-firing for parents that pass an
  // inline arrow function.
  const onDraftChangeRef = useRef(onDraftChange);
  useEffect(() => {
    onDraftChangeRef.current = onDraftChange;
  }, [onDraftChange]);
  // Single mutator for the draft sentence so every code path (typing,
  // escape, x-button, sentence-run, parent-sync) notifies the parent
  // via `onDraftChange`. The chip row in the parent listens to this
  // value so "Did you mean…?" suggestions appear mid-typing without
  // waiting for Enter or blur.
  const setDraft = (value: string) => {
    setDraftState(value);
    onDraftChangeRef.current?.(value);
  };
  // Surface the most recent parse failure inline (under the input) so
  // admins keep a clickable "Open AI billing" / "Open AI assist
  // settings" CTA after the toast disappears, mirroring the Catch Me
  // Up popover. We still fall back to plain text so results never go
  // away — this banner is purely informational/recovery.
  const [parseError, setParseError] = useState<FriendlyAiError | null>(null);
  // Declared up here (instead of next to the other render-time consts
  // ~200 lines down) because the useEffects below close over it. When
  // the const lived further down, the production minifier renamed it
  // to `H` and the effect's closure captured the binding before its
  // initializer ran — surfacing as "Cannot access 'H' before
  // initialization" only in the prod bundle.
  const showSmart = aiEnabledForWorkspace;
  // Keep the draft in sync when the parent clears the search externally.
  useEffect(() => {
    if (!smartEnabled) {
      setDraft(searchQuery || "");
    }
  }, [smartEnabled, searchQuery]);
  // Smart-search input is independent — when the user clears the spec we
  // also clear the draft sentence so the previous query doesn't linger.
  useEffect(() => {
    if (smartEnabled && spec === null && lastSpec.current !== null) {
      setDraft("");
    }
    lastSpec.current = spec;
  }, [smartEnabled, spec]);

  // Plain-text mode: debounce the draft into the committed `searchQuery`
  // so the spreadsheet filters live as the user types — no need to wait
  // for Enter or blur. Smart mode still waits for explicit submit because
  // each keystroke would otherwise burn an AI parse call.
  const onSearchQueryChangeRef = useRef(onSearchQueryChange);
  useEffect(() => {
    onSearchQueryChangeRef.current = onSearchQueryChange;
  }, [onSearchQueryChange]);
  useEffect(() => {
    // Always debounce into the text filter UNLESS an AI spec is currently
    // active (AI result owns the q). Typing resumes live text filtering
    // even in smart mode — AI only fires on explicit Enter / submit button.
    if (smartEnabled && showSmart && spec !== null) return;
    if (draft === (searchQuery || "")) return;
    const t = setTimeout(() => {
      onSearchQueryChangeRef.current(draft);
    }, 200);
    return () => clearTimeout(t);
  }, [draft, smartEnabled, showSmart, searchQuery, spec]);

  const parse = useMutation({
    mutationFn: async (sentence: string) => {
      const res = await apiRequest("POST", "/api/ai/nl-filter-parse", {
        sentence,
        page,
      });
      return (await res.json()) as {
        spec: NlFilterSpec;
        modelKey: string;
        costCents: number;
      };
    },
    onSuccess: (r, sentence) => {
      onSpecChange(r.spec);
      // Mirror the parser's `q` into the parent search-query so the
      // spreadsheet's existing substring filter still applies — same
      // contract as a manual search.
      //
      // Fallback logic: if the spec has an explicit `q`, use it. If the
      // spec has other structured filters (region, clientType, etc.), trust
      // those and clear the text search. But if the spec came back totally
      // empty (no q, no structured filters) — which happens when the AI
      // treats a plain name like "Belmont" as uninterpretable — fall back
      // to the typed sentence itself so the user still gets text results.
      let effectiveQ: string;
      if (typeof r.spec.q === "string") {
        effectiveQ = r.spec.q;
      } else {
        const hasStructuredFilters = (Object.entries(r.spec) as Array<[string, unknown]>).some(
          ([k, v]) =>
            k !== "q" &&
            v !== null &&
            v !== undefined &&
            !(Array.isArray(v) && v.length === 0),
        );
        effectiveQ = hasStructuredFilters ? "" : sentence;
      }
      onSearchQueryChange(effectiveQ);
      // A successful parse clears any inline error from a prior attempt.
      setParseError(null);
      // Refresh the AI balance pill in the header.
      queryClient.invalidateQueries({ queryKey: ["/api/ai/balance"] });
    },
    onError: async (e: unknown) => {
      const parsed = parseApiError(e);
      // If the gateway responded with a 402/403 but no JSON body
      // (e.g. an upstream proxy error page), the body's `code` will be
      // missing — fall back to the HTTP status so the user still gets
      // the tailored "out of credits" / "AI assist is off" message
      // instead of a generic toast.
      const code =
        parsed.code ??
        (parsed.status === 402
          ? "AI_BUDGET_EXCEEDED"
          : parsed.status === 403
            ? "AI_DISABLED"
            : undefined);
      const friendly = describeAiError(code, parsed.message, {
        isAdmin,
        defaultTitle: "Couldn't interpret search",
        defaultMessage: "We searched the text instead.",
      });

      // Smart-search always falls back to plain-text so the user still
      // gets results — but the toast text/CTA is shared with the rest
      // of the AI surface area, and we also pin a friendly inline
      // banner under the input so the recovery CTA stays put after the
      // toast disappears.
      if (code === "AI_BUDGET_EXCEEDED") {
        setParseError({
          ...friendly,
          message: `${friendly.message} Smart search is paused — falling back to plain text search.`,
        });
        toast({
          title: friendly.title,
          description: `${friendly.message} Smart search is paused — falling back to plain text search.`,
          variant: "destructive",
          action: friendly.action ? (
            <ToastAction
              altText={friendly.action.label}
              onClick={() => navigate(friendly.action!.href)}
            >
              {friendly.action.label}
            </ToastAction>
          ) : undefined,
        });
        onSearchQueryChange(draft);
        onSpecChange(null);
        return;
      }
      if (code === "AI_DISABLED") {
        setParseError(friendly);
        toast({
          title: friendly.title,
          description: friendly.message,
          variant: "destructive",
          action: friendly.action ? (
            <ToastAction
              altText={friendly.action.label}
              onClick={() => navigate(friendly.action!.href)}
            >
              {friendly.action.label}
            </ToastAction>
          ) : undefined,
        });
        onSmartEnabledChange(false);
        onSearchQueryChange(draft);
        return;
      }
      // Anything else (parse failures, provider errors) — non-destructive
      // because we still produced search results from the plain-text fallback.
      setParseError(friendly);
      toast({
        title: friendly.title,
        description: friendly.message,
      });
      onSearchQueryChange(draft);
      onSpecChange(null);
    },
  });

  const togglePref = useMutation({
    mutationFn: async (enabled: boolean) =>
      apiRequest("PATCH", "/api/profile/smart-search", { enabled }),
    onSuccess: (_r, enabled) => {
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
      onSmartEnabledChange(enabled);
      if (!enabled) {
        // Clear any active spec so we don't keep filtering on the AI
        // result after the user opted out.
        onSpecChange(null);
      }
    },
    onError: (e: any) => toast({ title: "Failed", description: e?.message, variant: "destructive" }),
  });

  // Saved searches (per-user, per-page). Only fetch when smart search is
  // enabled at the workspace level — there's nothing to save otherwise.
  const savedQuery = useQuery<SavedSmartSearch[]>({
    queryKey: ["/api/smart-search/saved", page],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/smart-search/saved?page=${page}`);
      return (await res.json()) as SavedSmartSearch[];
    },
    enabled: aiEnabledForWorkspace,
  });

  const saveMutation = useMutation({
    mutationFn: async (input: { name: string; sentence: string }) => {
      const res = await apiRequest("POST", "/api/smart-search/saved", {
        name: input.name,
        sentence: input.sentence,
        page,
      });
      return (await res.json()) as SavedSmartSearch;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/smart-search/saved", page] });
      toast({ title: "Search saved", description: "Re-run it any time from the saved list." });
    },
    onError: (e: any) =>
      toast({ title: "Couldn't save", description: e?.message || "Try again.", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/smart-search/saved/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/smart-search/saved", page] });
    },
    onError: (e: any) =>
      toast({ title: "Couldn't delete", description: e?.message || "Try again.", variant: "destructive" }),
  });

  // Local UI state for the popovers + name input.
  const [examplesOpen, setExamplesOpen] = useState(false);
  const [savedOpen, setSavedOpen] = useState(false);
  const [saveName, setSaveName] = useState("");

  const runSentence = (sentence: string) => {
    setDraft(sentence);
    setParseError(null);
    if (smartEnabled && aiEnabledForWorkspace) {
      parse.mutate(sentence);
    } else {
      onSearchQueryChange(sentence);
      onSpecChange(null);
    }
  };

  // Derive the chips to render from the active spec, dropping `q`
  // because it's already shown in the input.
  const activeChips = spec
    ? (Object.entries(spec) as Array<[keyof NlFilterSpec, unknown]>)
        .filter(([k, v]) => k !== "q" && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0))
    : [];

  const handleSubmit = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      onSearchQueryChange("");
      onSpecChange(null);
      setParseError(null);
      return;
    }
    setParseError(null);
    if (smartEnabled && aiEnabledForWorkspace) {
      parse.mutate(trimmed);
    } else {
      onSearchQueryChange(trimmed);
      onSpecChange(null);
    }
  };

  const removeChipKey = (k: keyof NlFilterSpec) => {
    if (!spec) return;
    const next: NlFilterSpec = { ...spec, [k]: null } as NlFilterSpec;
    // If every value is now empty, clear the spec entirely.
    const anyLeft = Object.entries(next).some(
      ([key, v]) => key !== "q" && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0),
    );
    onSpecChange(anyLeft ? next : null);
  };

  // `showSmart` is declared near the top of this component (above the
  // useEffects that depend on it) — see the comment there for why.
  const isParsing = parse.isPending;
  const examples = EXAMPLE_SENTENCES[page] || [];
  const saved = savedQuery.data || [];
  const canSaveCurrent = smartEnabled && showSmart && draft.trim().length > 0;

  return (
    <div className="flex flex-col gap-1.5 w-full sm:w-auto">
      <div
        className={cn(
          "flex items-center h-9 flex-1 sm:flex-none sm:w-80 min-w-0 rounded-md border border-input bg-background px-3",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          smartEnabled && showSmart && "border-primary/40",
        )}
        data-testid="smart-search-bar"
      >
        {smartEnabled && showSmart ? (
          <Sparkles className="h-4 w-4 text-primary shrink-0" aria-hidden="true" />
        ) : (
          <Search className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
        )}
        <input
          placeholder={
            smartEnabled && showSmart
              ? "Try: theaters in the South with no contact in 30 days"
              : placeholder || "Search…"
          }
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (parseError) setParseError(null);
            // Clear any active smart spec so typing re-enables the live
            // text debounce (user is signalling they want to search again).
            if (spec !== null) onSpecChange(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSubmit();
            }
            if (e.key === "Escape") {
              setDraft("");
              onSearchQueryChange("");
              onSpecChange(null);
              setParseError(null);
            }
          }}
          onBlur={() => {
            // Always sync on blur so the spreadsheet stays in sync
            // regardless of smart-search mode.
            onSearchQueryChange(draft);
          }}
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground ml-2 min-w-0 text-foreground"
          data-testid={`input-smart-search-${page}`}
          disabled={isParsing}
        />
        {isParsing && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground mr-1" />}
        {(draft || (spec && activeChips.length > 0)) && (
          <button
            type="button"
            className="shrink-0 ml-1 text-muted-foreground hover:text-foreground"
            onClick={() => {
              setDraft("");
              onSearchQueryChange("");
              onSpecChange(null);
              setParseError(null);
            }}
            data-testid="button-clear-smart-search"
            aria-label="Clear search"
          >
            <X className="h-3 w-3" />
          </button>
        )}
        {showSmart && smartEnabled && draft.trim().length > 0 && (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isParsing}
            className="shrink-0 ml-1 inline-flex items-center justify-center rounded px-1 h-6 text-primary hover:text-primary/80 hover-elevate active-elevate-2 disabled:opacity-50"
            aria-label="Run smart search"
            data-testid={`button-submit-smart-search-${page}`}
            title="Run smart search"
          >
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
        {showSmart && smartEnabled && (
          <Popover open={examplesOpen} onOpenChange={setExamplesOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="shrink-0 ml-1 inline-flex items-center gap-1 rounded px-1.5 h-6 text-[11px] font-medium text-muted-foreground hover:text-foreground hover-elevate active-elevate-2"
                title="Try an example sentence"
                aria-label="Try an example smart-search sentence"
                data-testid={`button-smart-search-examples-${page}`}
              >
                <Lightbulb className="h-3 w-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-80 p-2"
              data-testid={`popover-smart-search-examples-${page}`}
            >
              <div className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Try an example
              </div>
              <ul className="flex flex-col gap-0.5">
                {examples.map((s, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => {
                        setExamplesOpen(false);
                        runSentence(s);
                      }}
                      className="w-full text-left px-2 py-1.5 rounded text-sm hover-elevate active-elevate-2"
                      data-testid={`button-smart-search-example-${page}-${i}`}
                    >
                      {s}
                    </button>
                  </li>
                ))}
              </ul>
            </PopoverContent>
          </Popover>
        )}
        {showSmart && smartEnabled && (
          <Popover
            open={savedOpen}
            onOpenChange={(open) => {
              setSavedOpen(open);
              if (open) setSaveName("");
            }}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  "shrink-0 ml-1 inline-flex items-center gap-1 rounded px-1.5 h-6 text-[11px] font-medium hover-elevate active-elevate-2",
                  saved.length > 0 ? "text-primary" : "text-muted-foreground hover:text-foreground",
                )}
                title="Saved searches"
                aria-label="Saved smart searches"
                data-testid={`button-smart-search-saved-${page}`}
              >
                <Bookmark className="h-3 w-3" />
                {saved.length > 0 && (
                  <span className="text-[10px] leading-none" data-testid={`text-smart-search-saved-count-${page}`}>
                    {saved.length}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-80 p-2"
              data-testid={`popover-smart-search-saved-${page}`}
            >
              <div className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Saved searches
              </div>
              {savedQuery.isLoading ? (
                <div className="px-2 py-2 text-xs text-muted-foreground">Loading…</div>
              ) : saved.length === 0 ? (
                <div className="px-2 py-2 text-xs text-muted-foreground">
                  No saved searches yet. Type a sentence above and save it below.
                </div>
              ) : (
                <ul className="flex flex-col gap-0.5 max-h-64 overflow-auto">
                  {saved.map((s) => (
                    <li
                      key={s.id}
                      className="flex items-start gap-1 rounded hover-elevate active-elevate-2"
                      data-testid={`row-smart-search-saved-${s.id}`}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setSavedOpen(false);
                          runSentence(s.sentence);
                        }}
                        className="flex-1 text-left px-2 py-1.5 min-w-0"
                        data-testid={`button-run-saved-${s.id}`}
                      >
                        <div className="text-sm font-medium truncate" data-testid={`text-saved-name-${s.id}`}>
                          {s.name}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate" data-testid={`text-saved-sentence-${s.id}`}>
                          {s.sentence}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteMutation.mutate(s.id)}
                        className="shrink-0 mr-1 mt-1.5 p-1 rounded text-muted-foreground hover:text-destructive"
                        title={`Delete "${s.name}"`}
                        aria-label={`Delete saved search ${s.name}`}
                        data-testid={`button-delete-saved-${s.id}`}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-2 border-t pt-2 px-1">
                <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-1">
                  Save current search
                </div>
                {canSaveCurrent ? (
                  <form
                    className="flex items-center gap-1"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const name = saveName.trim();
                      if (!name) return;
                      saveMutation.mutate(
                        { name, sentence: draft.trim() },
                        {
                          onSuccess: () => {
                            setSaveName("");
                            setSavedOpen(false);
                          },
                        },
                      );
                    }}
                  >
                    <Input
                      value={saveName}
                      onChange={(e) => setSaveName(e.target.value)}
                      placeholder="Name this search…"
                      className="h-8 text-sm"
                      maxLength={120}
                      data-testid={`input-save-search-name-${page}`}
                    />
                    <Button
                      type="submit"
                      size="sm"
                      className="h-8 shrink-0"
                      disabled={saveMutation.isPending || saveName.trim().length === 0}
                      data-testid={`button-save-search-${page}`}
                    >
                      <BookmarkPlus className="h-3.5 w-3.5 mr-1" />
                      Save
                    </Button>
                  </form>
                ) : (
                  <p className="text-[11px] text-muted-foreground px-1">
                    Type a smart-search sentence above, then come back here to save it.
                  </p>
                )}
              </div>
            </PopoverContent>
          </Popover>
        )}
        {showSmart && (
          <button
            type="button"
            onClick={() => togglePref.mutate(!smartEnabled)}
            className={cn(
              "shrink-0 ml-1 inline-flex items-center gap-1 rounded px-1.5 h-6 text-[11px] font-medium transition-colors",
              "hover-elevate active-elevate-2",
              smartEnabled
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
            title={smartEnabled ? "Smart search on. Click to switch to plain text." : "Smart search off. Click to enable."}
            data-testid="button-toggle-smart-search"
            disabled={togglePref.isPending}
          >
            <Sparkles className="h-3 w-3" />
            Smart
          </button>
        )}
      </div>
      {parseError && (
        <div
          className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2"
          data-testid={`smart-search-error-${page}`}
        >
          <div className="flex-1 min-w-0 space-y-1">
            <div
              className="text-xs font-medium text-destructive"
              data-testid={`text-smart-search-error-title-${page}`}
            >
              {parseError.title}
            </div>
            <div
              className="text-xs text-muted-foreground"
              data-testid={`text-smart-search-error-${page}`}
            >
              {parseError.message}
            </div>
            {parseError.hint && (
              <div
                className="text-[11px] text-muted-foreground"
                data-testid={`text-smart-search-error-hint-${page}`}
              >
                {parseError.hint}
              </div>
            )}
            {parseError.action && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs mt-1"
                onClick={() => navigate(parseError.action!.href)}
                data-testid={`button-smart-search-error-action-${page}`}
              >
                {parseError.action.label}
                <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            )}
          </div>
          <button
            type="button"
            className="shrink-0 text-muted-foreground hover:text-foreground"
            onClick={() => setParseError(null)}
            aria-label="Dismiss"
            data-testid={`button-dismiss-smart-search-error-${page}`}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
      {spec && activeChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="smart-search-chips">
          {activeChips.map(([k, v]) => (
            <button
              key={k}
              type="button"
              onClick={() => removeChipKey(k)}
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 h-6 text-[11px] font-medium hover-elevate active-elevate-2"
              title={`Remove ${SPEC_KEY_LABEL[k] || k} filter`}
              data-testid={`chip-${page}-${String(k)}`}
            >
              <span className="opacity-70">{SPEC_KEY_LABEL[k] || k}:</span>
              <span>{formatChipValue(k, v, regions)}</span>
              <X className="h-2.5 w-2.5" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
